import React from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, CreditCard, Package, Clock, AlertCircle, CheckCircle, XCircle, Phone, Mail } from 'lucide-react';

const ReturnPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-pink-600 to-purple-700 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-6">
              <RefreshCw className="h-8 w-8" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Return & Refund Policy</h1>
            <p className="text-xl text-pink-100">
              Our commitment to quality and customer satisfaction
            </p>
          </div>
        </div>
      </div>

      <div className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Policy Overview */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <div>
                  <h2 className="text-3xl font-bold mb-6">Our Return Policy</h2>
                  <p className="text-gray-600 mb-6">
                    At Sohkin Flowers, we strive to deliver the freshest flowers and highest quality products. 
                    However, if you're not completely satisfied with your purchase, we're here to help.
                  </p>
                  <div className="bg-pink-50 p-6 rounded-xl">
                    <div className="flex items-center mb-4">
                      <Clock className="h-6 w-6 text-pink-600 mr-3" />
                      <h3 className="text-xl font-bold">Return Window</h3>
                    </div>
                    <p className="text-gray-700">
                      You have <span className="font-bold">24 hours</span> from the time of delivery to report any issues with your order.
                    </p>
                  </div>
                </div>

                <div>
                  <h2 className="text-3xl font-bold mb-6">Quick Summary</h2>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                      <div>
                        <div className="font-bold">Eligible for Return</div>
                        <div className="text-sm text-gray-600">Damaged, defective, or incorrect items</div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <XCircle className="h-5 w-5 text-red-500 mr-3 mt-1 flex-shrink-0" />
                      <div>
                        <div className="font-bold">Not Eligible</div>
                        <div className="text-sm text-gray-600">Change of mind, seasonal flowers</div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <CreditCard className="h-5 w-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                      <div>
                        <div className="font-bold">Refund Processing</div>
                        <div className="text-sm text-gray-600">5-7 business days after approval</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Return Conditions */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-6">Conditions for Returns</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-green-50 p-6 rounded-xl border border-green-200">
                    <div className="flex items-center mb-4">
                      <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
                      <h4 className="text-xl font-bold">When Returns Are Accepted</h4>
                    </div>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full bg-green-600 mt-2 mr-3 flex-shrink-0"></div>
                        <span>Flowers arrive damaged or wilted</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full bg-green-600 mt-2 mr-3 flex-shrink-0"></div>
                        <span>Incorrect item delivered</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full bg-green-600 mt-2 mr-3 flex-shrink-0"></div>
                        <span>Missing items from combo packs</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full bg-green-600 mt-2 mr-3 flex-shrink-0"></div>
                        <span>Quality issues with non-flower items</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-red-50 p-6 rounded-xl border border-red-200">
                    <div className="flex items-center mb-4">
                      <XCircle className="h-6 w-6 text-red-600 mr-3" />
                      <h4 className="text-xl font-bold">When Returns Are Not Accepted</h4>
                    </div>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full bg-red-600 mt-2 mr-3 flex-shrink-0"></div>
                        <span>Change of mind or personal preference</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full bg-red-600 mt=2 mr-3 flex-shrink-0"></div>
                        <span>Flowers that have naturally wilted after 7 days</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full bg-red-600 mt-2 mr-3 flex-shrink-0"></div>
                        <span>Personalized or custom-made items</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 rounded-full bg-red-600 mt-2 mr-3 flex-shrink-0"></div>
                        <span>Seasonal flowers with natural variations</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Return Process */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-6">How to Initiate a Return</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="text-center p-6 bg-gray-50 rounded-xl">
                    <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center mx-auto mb-4">
                      <span className="text-xl font-bold text-pink-600">1</span>
                    </div>
                    <h4 className="font-bold mb-2">Contact Support</h4>
                    <p className="text-sm text-gray-600">Email or call within 24 hours of delivery</p>
                  </div>
                  <div className="text-center p-6 bg-gray-50 rounded-xl">
                    <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center mx-auto mb-4">
                      <span className="text-xl font-bold text-pink-600">2</span>
                    </div>
                    <h4 className="font-bold mb-2">Provide Details</h4>
                    <p className="text-sm text-gray-600">Share order details and photos of issue</p>
                  </div>
                  <div className="text-center p-6 bg-gray-50 rounded-xl">
                    <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center mx-auto mb-4">
                      <span className="text-xl font-bold text-pink-600">3</span>
                    </div>
                    <h4 className="font-bold mb-2">Get Approval</h4>
                    <p className="text-sm text-gray-600">Our team reviews and approves return</p>
                  </div>
                  <div className="text-center p-6 bg-gray-50 rounded-xl">
                    <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center mx-auto mb-4">
                      <span className="text-xl font-bold text-pink-600">4</span>
                    </div>
                    <h4 className="font-bold mb-2">Resolution</h4>
                    <p className="text-sm text-gray-600">Receive replacement or refund</p>
                  </div>
                </div>
              </div>

              {/* Refund Information */}
              <div className="bg-blue-50 p-8 rounded-xl mb-12">
                <h3 className="text-2xl font-bold mb-6 flex items-center">
                  <CreditCard className="h-6 w-6 text-blue-600 mr-3" />
                  Refund Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-bold mb-4">Refund Methods</h4>
                    <ul className="space-y-3">
                      <li className="flex items-center">
                        <div className="w-2 h-2 rounded-full bg-blue-600 mr-3"></div>
                        <span>Original payment method (5-7 business days)</span>
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 rounded-full bg-blue-600 mr-3"></div>
                        <span>Store credit (instant)</span>
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 rounded-full bg-blue-600 mr-3"></div>
                        <span>Bank transfer (3-5 business days)</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold mb-4">Refund Amount</h4>
                    <ul className="space-y-3">
                      <li className="flex items-center">
                        <div className="w-2 h-2 rounded-full bg-blue-600 mr-3"></div>
                        <span>Full refund for damaged/incorrect items</span>
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 rounded-full bg-blue-600 mr-3"></div>
                        <span>Partial refund for partial issues</span>
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 rounded-full bg-blue-600 mr-3"></div>
                        <span>Shipping charges refunded for our errors</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Important Notes */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                <div className="flex items-start">
                  <AlertCircle className="h-6 w-6 text-yellow-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="text-lg font-bold mb-2">Important Information</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li>• Please take clear photos of damaged items as proof for return claims</li>
                      <li>• For perishable items, we may offer replacement instead of return</li>
                      <li>• Personalized items (engraved, custom-made) cannot be returned</li>
                      <li>• Return requests must be made within 24 hours of delivery</li>
                      <li>• Seasonal flower variations are not considered defects</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Section */}
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-6">Need Help with Returns?</h2>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Our customer support team is here to help you with any return or refund queries
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:18001234567"
                  className="inline-flex items-center justify-center bg-pink-600 text-white px-8 py-4 rounded-full font-bold hover:bg-pink-700 transition-colors"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Call Support: 1800-123-4567
                </a>
                <a
                  href="mailto:returns@sohkinflowers.com"
                  className="inline-flex items-center justify-center border-2 border-pink-600 text-pink-600 px-8 py-4 rounded-full font-bold hover:bg-pink-50 transition-colors"
                >
                  <Mail className="mr-2 h-5 w-5" />
                  Email Returns Team
                </a>
              </div>
              <p className="mt-6 text-gray-600">
                Response time: Within 2 hours during business hours
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReturnPolicy;